import { durationOf, formatTime, peaks } from '../record/audio-data';
import type { Take } from '../record/takes-store';

interface Handlers {
  toggle(take: Take, at?: number): void;
  exportWav(take: Take): void;
  exportMp3(take: Take, button: HTMLButtonElement): void;
  exportVideo(take: Take): void;
  remove(take: Take): void;
  /** Synchronous: drag-and-drop needs the file URL before dragstart returns. */
  dragFile(take: Take): { url: string; filename: string };
}

interface Row {
  take: Take;
  li: HTMLLIElement;
  canvas: HTMLCanvasElement;
  peaks: Float32Array;
  progress: number;
}

const BUCKETS = 600;

export class TakesView {
  private rows = new Map<string, Row>();
  private playing: string | null = null;

  constructor(
    private list: HTMLOListElement,
    private empty: HTMLElement,
    private h: Handlers,
  ) {
    new ResizeObserver(() => this.rows.forEach((r) => this.draw(r))).observe(list);
    // Waveform colors come from CSS, so repaint when the theme flips.
    new MutationObserver(() => this.rows.forEach((r) => this.draw(r))).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => this.rows.forEach((r) => this.draw(r)));
  }

  get count(): number {
    return this.rows.size;
  }

  add(take: Take, atTop = true): void {
    const li = document.createElement('li');
    li.className = 'take';
    li.dataset.id = take.id;
    li.innerHTML = `
      <button type="button" class="take__play" aria-label="Play ${esc(take.name)}">
        <svg class="i-play" viewBox="0 0 20 20" aria-hidden="true"><path d="M6 4.5v11l9-5.5z" fill="currentColor"/></svg>
        <svg class="i-stop" viewBox="0 0 20 20" aria-hidden="true"><rect x="5.5" y="5.5" width="9" height="9" rx="1.5" fill="currentColor"/></svg>
      </button>
      <div class="take__wave" draggable="true" title="Click to play from here. Drag into your DAW or onto the desktop.">
        <canvas></canvas>
      </div>
      <div class="take__info">
        <span class="take__name">${esc(take.name)}</span>
        <span class="take__meta">${esc(take.presetName)} · ${formatTime(durationOf(take.audio))}${take.video ? ' · with video' : ''}</span>
      </div>
      <div class="take__actions">
        <button type="button" class="chip" data-act="wav" title="24-bit WAV, lossless">WAV</button>
        <button type="button" class="chip" data-act="mp3" title="320 kbps MP3">MP3</button>
        ${take.video ? `<button type="button" class="chip chip--accent" data-act="video" title="Video with sound">${take.video.ext.toUpperCase()}</button>` : ''}
        <button type="button" class="chip chip--ghost" data-act="delete" aria-label="Delete ${esc(take.name)}" title="Delete">
          <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true"><path d="M5 5l10 10M15 5 5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
        </button>
      </div>`;
    const canvas = li.querySelector('canvas')!;
    const row: Row = { take, li, canvas, peaks: peaks(take.audio, BUCKETS), progress: 0 };
    this.rows.set(take.id, row);

    li.querySelector<HTMLButtonElement>('.take__play')!.addEventListener('click', () => this.h.toggle(take));
    const wave = li.querySelector<HTMLElement>('.take__wave')!;
    wave.addEventListener('click', (e) => {
      const r = wave.getBoundingClientRect();
      this.h.toggle(take, (e.clientX - r.left) / r.width);
    });
    wave.addEventListener('dragstart', (e) => {
      if (!e.dataTransfer) return;
      const { url, filename } = this.h.dragFile(take);
      // Chrome and Edge turn this into a real file on drop (desktop, DAWs).
      e.dataTransfer.setData('DownloadURL', `audio/wav:${filename}:${url}`);
      e.dataTransfer.setData('text/uri-list', url);
      e.dataTransfer.effectAllowed = 'copy';
    });
    li.querySelector('.take__actions')!.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-act]');
      if (!btn) return;
      const act = btn.dataset.act;
      if (act === 'wav') this.h.exportWav(take);
      else if (act === 'mp3') this.h.exportMp3(take, btn);
      else if (act === 'video') this.h.exportVideo(take);
      else if (act === 'delete') this.h.remove(take);
    });

    if (atTop) this.list.prepend(li);
    else this.list.append(li);
    this.empty.hidden = true;
    requestAnimationFrame(() => this.draw(row));
    if (atTop) {
      li.classList.add('is-new');
      setTimeout(() => li.classList.remove('is-new'), 900);
    }
  }

  remove(id: string): void {
    const row = this.rows.get(id);
    if (!row) return;
    row.li.remove();
    this.rows.delete(id);
    this.empty.hidden = this.rows.size > 0;
  }

  setPlayback(id: string | null, progress = 0): void {
    if (this.playing && this.playing !== id) {
      const prev = this.rows.get(this.playing);
      if (prev) {
        prev.li.classList.remove('is-playing');
        prev.progress = 0;
        this.draw(prev);
      }
    }
    this.playing = id;
    if (!id) return;
    const row = this.rows.get(id);
    if (!row) return;
    row.li.classList.add('is-playing');
    row.progress = progress;
    this.draw(row);
  }

  private draw(row: Row): void {
    const { canvas } = row;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return;
    if (canvas.width !== Math.round(w * dpr)) canvas.width = Math.round(w * dpr);
    if (canvas.height !== Math.round(h * dpr)) canvas.height = Math.round(h * dpr);
    const g = canvas.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    const style = getComputedStyle(row.li);
    const inkColor = style.getPropertyValue('--ink').trim() || '#222';
    const faint = style.getPropertyValue('--ink-3').trim() || '#999';
    let max = 0.0001;
    for (let i = 0; i < row.peaks.length; i++) max = Math.max(max, Math.abs(row.peaks[i]));
    const scale = (h / 2 - 2) / max;
    const bars = Math.max(20, Math.floor(w / 3));
    const mid = h / 2;
    for (let b = 0; b < bars; b++) {
      const i0 = Math.floor((b / bars) * BUCKETS);
      const i1 = Math.max(i0 + 1, Math.floor(((b + 1) / bars) * BUCKETS));
      let lo = 0;
      let hi = 0;
      for (let i = i0; i < i1; i++) {
        lo = Math.min(lo, row.peaks[i * 2]);
        hi = Math.max(hi, row.peaks[i * 2 + 1]);
      }
      const x = b * 3;
      const top = Math.max(1, hi * scale);
      const bot = Math.max(1, -lo * scale);
      g.fillStyle = b / bars < row.progress ? inkColor : faint;
      g.fillRect(x, mid - top, 2, top + bot);
    }
    if (row.progress > 0) {
      g.fillStyle = inkColor;
      g.fillRect(Math.round(row.progress * w), 0, 1.5, h);
    }
  }
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
